import { Alert, Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import CustomSearchBox from 'components/app-container/custom-search-box/CustomSearchBox';
import UPButton from 'components/button';
import { UPDataGrid } from 'components/data-grid';
import DeleteModal from 'components/delete-modal';
import { UPDropdownItem } from 'components/dropdown';
import CATEGORIES from 'constants/Categories';
import { CompanyRole } from 'constants/CompanyRole';
import { useGridSearch } from 'hooks/useGridSearch';
import { toNumber } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import { deleteFunctionsAction, getFunctionsAction, setDeleteWarning } from 'store/actions/company-functions-actions';
import { getParitairComiteSelectItemsAction } from 'store/actions/paritair-comite-actions';
import { functionsDeleteWarningSelector, functionsSelector } from 'store/selectors/company-functions-selectors';
import { paritairComiteSelectItemsSelector } from 'store/selectors/paritair-comite-selectors';
import { profileSelector } from 'store/selectors/profile-selectors';
import IPayrollFunction from 'types/company/payroll/IPayrollFunction';
import { ReactComponent as CloseIcon } from '../../../assets/icons/Close.svg';
import {
  COMPANY_PAYROLL__ADD_NEW_FUNCTION,
  COMPANY_PAYROLL__CATEGORY,
  COMPANY_PAYROLL__FUNCTION_DESCRIPTION,
  COMPANY_PAYROLL__FUNCTION_WARNING_ON_DELETE,
  COMPANY_PAYROLL__PARITAIR_COMITE,
  GENERAL__DELETE_SELECTED,
  GENERAL__EDIT,
} from '../../../translations/constants';
import { CloseAlertIcon } from '../styles';
import EditPayrollFunction from './EditPayrollFunction';
import { PayrollTabActionButton, PayrollTabAlerts, PayrollTabContainer } from './styles';
import EmployeeFunctionLinks from './employee-function-links/EmployeeFunctionLinks';
import useFunctionDeleteConfirmation from './employee-function-links/useFunctionDeleteConfirmation';

const PayrollFunctions = (): JSX.Element => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams();

  const userIsCompanyManager = useAppSelector(profileSelector).role === CompanyRole.COMPANY_MANAGER;
  const functions = useAppSelector(functionsSelector);
  const paritairComites = useAppSelector(paritairComiteSelectItemsSelector);
  const deleteReturnedWarning = useAppSelector(functionsDeleteWarningSelector);

  const [rowOnHover, setRowOnHover] = useState<number>();
  const [openEditPayrollFunction, setOpenEditPayrollFunction] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const {
    open: deleteConfirmationOpen,
    message: deleteConfirmationMessage,
    requestDelete,
    close: closeDeleteConfirmation,
  } = useFunctionDeleteConfirmation(functions ?? []);

  const categories = useMemo((): UPDropdownItem[] => {
    return CATEGORIES;
  }, []);

  useEffect(() => {
    dispatch(getFunctionsAction(toNumber(companyId)));
    dispatch(getParitairComiteSelectItemsAction());
    dispatch(setDeleteWarning(false));
  }, [companyId, dispatch]);

  const onOpenModal = useCallback(event => {
    event.preventDefault();
    event.stopPropagation();
    setOpenEditPayrollFunction(true);
  }, []);

  const onOpenDeleteModal = useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();
      requestDelete(selectedRows);
    },
    [requestDelete, selectedRows],
  );

  const onSelectionChange = useCallback((ids: Iterable<number>) => {
    const selectedIDs = Array.from(ids);
    setSelectedRows(selectedIDs);
  }, []);

  const matches = useCallback(
    (row: IPayrollFunction, term: string) => {
      const needle = term.toLowerCase();
      const paritairLabel =
        paritairComites?.find(p => p.value?.toString() === row.paritairComiteId?.toString())?.label ?? '';
      const categoryLabel = categories?.find(c => c.value === row.category)?.label ?? '';
      return (
        (row.description ?? '').toLowerCase().includes(needle) ||
        paritairLabel.toLowerCase().includes(needle) ||
        categoryLabel.toLowerCase().includes(needle)
      );
    },
    [paritairComites, categories],
  );

  const { onSearch, filteredRows } = useGridSearch(functions ?? [], matches);

  const getFunctionOnEdit = useCallback((): IPayrollFunction => {
    if (rowOnHover) {
      return functions.find(payrollFunction => payrollFunction.id === rowOnHover);
    }
    const newFunction: IPayrollFunction = {
      id: 0,
      description: '',
      companyId: toNumber(companyId),
      category: '',
      paritairComiteId: '',
      type: '',
      color: '',
      class: '',
    };
    return newFunction;
  }, [rowOnHover, functions, companyId]);

  const columns: GridColDef[] = [
    { field: 'id', headerName: '', hide: true },
    { field: 'description', headerName: t(COMPANY_PAYROLL__FUNCTION_DESCRIPTION), width: 200 },
    {
      field: 'paritairComiteId',
      headerName: t(COMPANY_PAYROLL__PARITAIR_COMITE),
      width: 250,
      valueGetter: params => {
        const paritairComite = paritairComites.find(p => p.value === params.row.paritairComiteId);
        return paritairComite.label;
      },
    },
    {
      field: 'category',
      headerName: t(COMPANY_PAYROLL__CATEGORY),
      width: 250,
      valueGetter: params => {
        const category = categories.find(c => params.row.category === c.value);
        return category?.label;
      },
    },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      width: 100,
      sortable: false,
      disableColumnMenu: true,
      renderCell: params => {
        if (rowOnHover === params.id) {
          return <UPButton onClick={onOpenModal} text={t(GENERAL__EDIT)} disabled={userIsCompanyManager} outlined />;
        }
        return null;
      },
    },
  ];

  return (
    <PayrollTabContainer>
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <PayrollTabActionButton
          text={`+ ${t(COMPANY_PAYROLL__ADD_NEW_FUNCTION)}`}
          onClick={event => {
            setRowOnHover(null);
            onOpenModal(event);
          }}
          disabled={userIsCompanyManager}
        />
        <Box sx={{ marginLeft: '15px' }}>
          <PayrollTabActionButton
            text={t(GENERAL__DELETE_SELECTED)}
            onClick={onOpenDeleteModal}
            outlined
            disabled={selectedRows.length <= 0 || userIsCompanyManager}
          />
        </Box>
        <Box display="flex" marginLeft={2} alignItems="center">
          <CustomSearchBox onSearch={onSearch} />
        </Box>
      </Box>
      <Box sx={{ height: '400px', width: '100%' }}>
        <UPDataGrid
          rows={filteredRows}
          columns={columns}
          setRowOnHover={setRowOnHover}
          onSelectionModelChange={onSelectionChange}
          withCheckboxes={!userIsCompanyManager}
        />
        {openEditPayrollFunction && (
          <EditPayrollFunction
            open={openEditPayrollFunction}
            onClose={() => setOpenEditPayrollFunction(false)}
            payrollFunction={getFunctionOnEdit()}
          />
        )}
        {deleteConfirmationOpen && (
          <DeleteModal
            content={deleteConfirmationMessage}
            open={deleteConfirmationOpen}
            onClose={closeDeleteConfirmation}
            onDelete={() => dispatch(deleteFunctionsAction({ companyId, functionIds: selectedRows }))}
          />
        )}
      </Box>
      <PayrollTabAlerts>
        {deleteReturnedWarning && (
          <Alert
            severity="error"
            icon={false}
            variant="filled"
            sx={{ width: '800px' }}
            action={
              <CloseAlertIcon
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  dispatch(setDeleteWarning(false));
                }}
              >
                <CloseIcon fill="white" />
              </CloseAlertIcon>
            }
          >
            {t(COMPANY_PAYROLL__FUNCTION_WARNING_ON_DELETE)}
          </Alert>
        )}
      </PayrollTabAlerts>
      <EmployeeFunctionLinks />
    </PayrollTabContainer>
  );
};

export default PayrollFunctions;
